/** @jsxImportSource @emotion/react */
import { useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';
import { getResolvedContractorId, getTasksOnScopeReport } from '@kaiizen/kai-scan-template-system';
import { useState } from 'react';

import { ExpandCaret } from '@/Client/Icons';
import { contractorCanPerformTrade, getTaskTradeForContractorAssignment } from '@/Client/Lib/contractorTradeLib';
import { getTradeDisplayName } from '@/Client/Partials/Projects/contractorTradeList';
import type { FocusedProjectOverviewPanelStyles } from './focusedProjectOverviewPanelStyles';

const presentTaskCount = (count: number) => `${count} ${count === 1 ? 'task' : 'tasks'}`;

/** A collapsible contractor cascade with inline editing for the default contractor of each trade. */
export const ProjectOverviewContractorAssignments = ({ canEdit, styles }: {
  canEdit: boolean;
  styles: FocusedProjectOverviewPanelStyles;
}) => {
  const [assignmentsOpen, setAssignmentsOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const { contractors = [], contractorsMap, scanDataMutators, scanRoot, taskIdsByAssignedContractorId } = useScanEditingContextSlice([
    'contractors',
    'contractorsMap',
    'scanDataMutators',
    'scanRoot',
    'taskIdsByAssignedContractorId',
  ]);
  const defaultContractorsByTrade = scanRoot?.defaultContractorsByTrade ?? {};
  const generalContractorId = defaultContractorsByTrade.general;
  const tasks = scanRoot ? getTasksOnScopeReport(scanRoot) : [];
  const tradeKeys = [
    ...new Set([
      ...Object.keys(defaultContractorsByTrade),
      ...tasks.map(task => getTaskTradeForContractorAssignment(task)),
    ]),
  ].filter(trade => trade !== 'general');
  const tradeAssignments = tradeKeys.map(trade => {
    const tasksForTrade = tasks.filter(task => getTaskTradeForContractorAssignment(task) === trade);
    const defaultContractorId = defaultContractorsByTrade[trade];
    const resolvedContractorId = getResolvedContractorId(trade, undefined, defaultContractorsByTrade);
    const taskOverridesByContractorId = tasksForTrade.reduce((taskCountByContractorId, task) => {
      if (!task.assignedContractorId || task.assignedContractorId === resolvedContractorId) return taskCountByContractorId;
      taskCountByContractorId[task.assignedContractorId] = (taskCountByContractorId[task.assignedContractorId] ?? 0) + 1;
      return taskCountByContractorId;
    }, {} as Record<string, number>);
    return {
      contractorName: resolvedContractorId ? contractorsMap?.[resolvedContractorId]?.name ?? 'Unknown contractor' : 'Unassigned',
      isInherited: !defaultContractorId && !!generalContractorId,
      taskOverrides: Object.entries(taskOverridesByContractorId).map(([contractorId, taskCount]) => ({
        contractorId,
        contractorName: contractorsMap?.[contractorId]?.name ?? 'Unknown contractor',
        taskCount,
      })),
      taskCount: tasksForTrade.length,
      trade,
    };
  });
  const unassignedTaskCount = tasks.filter(task =>
    !getResolvedContractorId(
      getTaskTradeForContractorAssignment(task),
      task.assignedContractorId,
      defaultContractorsByTrade,
    )
  ).length;
  const generalTaskCount = generalContractorId ? taskIdsByAssignedContractorId?.[generalContractorId]?.length ?? 0 : 0;
  const generalContractorName = generalContractorId
    ? contractorsMap?.[generalContractorId]?.name ?? 'Unknown contractor'
    : 'Unassigned';
  const hasAssignments = generalContractorId
    || tradeAssignments.some(assignment => assignment.contractorName !== 'Unassigned' || assignment.taskOverrides.length > 0);
  const editableTrades = ['general', ...tradeKeys];

  const updateDefaultContractor = (trade: string, contractorId: string) => {
    if (!canEdit || !scanRoot) return;
    const nextDefaultContractorsByTrade = { ...defaultContractorsByTrade };
    if (contractorId) nextDefaultContractorsByTrade[trade] = contractorId;
    else delete nextDefaultContractorsByTrade[trade];
    void scanDataMutators.updateScan({ defaultContractorsByTrade: nextDefaultContractorsByTrade });
  };

  return (
    <section css={styles.contractorModule} aria-labelledby='project-overview-contractors'>
      <div css={styles.moduleHeader}>
        <h2 id='project-overview-contractors' css={styles.moduleTitle}>Contractor assignments</h2>
        <div css={styles.moduleActions}>
          {canEdit && (
            <button
              aria-label={editing ? 'Done editing contractor assignments' : 'Edit contractor assignments'}
              css={styles.moduleAction}
              type='button'
              onClick={() => {
                setAssignmentsOpen(true);
                setEditing(value => !value);
              }}
            >
              {editing ? 'Done' : 'Edit'}
            </button>
          )}
          <button
            aria-controls='project-overview-contractor-body'
            aria-expanded={assignmentsOpen}
            aria-label={assignmentsOpen ? 'Hide contractor assignments' : 'Show contractor assignments'}
            css={styles.moduleToggle}
            type='button'
            onClick={() => setAssignmentsOpen(open => !open)}
          >
            <ExpandCaret expanded={assignmentsOpen} initialRotation={0} finalRotation={180} />
          </button>
        </div>
      </div>
      {assignmentsOpen && editing
        ? (
          <div id='project-overview-contractor-body' css={styles.contractorEditor}>
            {editableTrades.map(trade => (
              <label key={trade}>
                <span css={styles.detailLabel}>{getTradeDisplayName(trade)}</span>
                <select
                  aria-label={`${getTradeDisplayName(trade)} contractor`}
                  css={styles.detailSelect}
                  value={defaultContractorsByTrade[trade] ?? ''}
                  onChange={event => updateDefaultContractor(trade, event.target.value)}
                >
                  <option value=''>Unassigned</option>
                  {contractors
                    .filter(contractor => contractorCanPerformTrade(contractor.trades, trade))
                    .sort((left, right) => left.name.localeCompare(right.name))
                    .map(contractor => <option key={contractor.id} value={contractor.id}>{contractor.name}</option>)}
                </select>
              </label>
            ))}
          </div>
        )
        : assignmentsOpen && hasAssignments
        ? (
          <div id='project-overview-contractor-body' css={styles.contractorBody}>
            <div css={styles.contractorGeneral}>
              <div>
                <div css={styles.contractorSummaryLabel}>General contractor</div>
                <div css={styles.contractorSummaryValue}>{generalContractorName}</div>
              </div>
              <span css={styles.contractorTaskCount}>{presentTaskCount(generalTaskCount)}</span>
            </div>
            {tradeAssignments.length > 0 && (
              <ul id='project-overview-contractor-trades' css={styles.contractorList}>
                {tradeAssignments.map(assignment => (
                  <li css={styles.contractorRow} key={assignment.trade}>
                    <div css={styles.contractorRowContent}>
                      <span css={styles.contractorName}>{getTradeDisplayName(assignment.trade)}</span>
                      <span css={styles.contractorProvenance}>
                        {assignment.isInherited
                          ? `From general · ${assignment.contractorName}`
                          : assignment.contractorName !== 'Unassigned'
                          ? `Trade default · ${assignment.contractorName}`
                          : 'No trade default'}
                      </span>
                      <span css={styles.contractorTaskCount}>{presentTaskCount(assignment.taskCount)}</span>
                    </div>
                    {assignment.taskOverrides.length > 0 && (
                      <ul css={styles.contractorOverrideList}>
                        {assignment.taskOverrides.map(taskOverride => (
                          <li css={styles.contractorRow} key={taskOverride.contractorId}>
                            <div css={styles.contractorRowContent}>
                              <span css={styles.contractorName}>{getTradeDisplayName(assignment.trade)}</span>
                              <span css={styles.contractorProvenance}>Task override · {taskOverride.contractorName}</span>
                              <span css={styles.contractorTaskCount}>{presentTaskCount(taskOverride.taskCount)}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {unassignedTaskCount > 0 && (canEdit
              ? (
                <button
                  css={styles.contractorUnassigned}
                  type='button'
                  onClick={() => {
                    setAssignmentsOpen(true);
                    setEditing(true);
                  }}
                >
                  <span>Unassigned contractors</span>
                  <span css={styles.contractorTaskCount}>{presentTaskCount(unassignedTaskCount)}</span>
                </button>
              )
              : (
                <div css={styles.contractorUnassigned}>
                  <span>Unassigned contractors</span>
                  <span css={styles.contractorTaskCount}>{presentTaskCount(unassignedTaskCount)}</span>
                </div>
              ))}
          </div>
        )
        : assignmentsOpen
        ? <div id='project-overview-contractor-body' css={styles.moduleEmpty}>No contractors assigned</div>
        : null}
    </section>
  );
};
